import { type ClassValue, clsx } from "clsx";
import Cookies from "js-cookie";
import ky from "ky";
import { twMerge } from "tailwind-merge";

import { useDefaultStore } from "@/stores";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * 단어의 마지막 글자 받침 유무에 따라 알맞은 조사를 붙여 반환한다.
 * 받침이 있으면 첫 번째 조사(을/이/은), 없으면 두 번째 조사(를/가/는)를 사용한다.
 */
export function withJosa(
	word: string,
	withFinal: string,
	withoutFinal: string,
): string {
	const lastChar = word.at(-1);
	if (!lastChar) {
		return `${word}${withoutFinal}`;
	}
	const code = lastChar.charCodeAt(0);
	// 한글 음절 영역이 아니면 받침 없는 것으로 처리
	const isHangul = code >= 0xac00 && code <= 0xd7a3;
	const hasFinal = isHangul && (code - 0xac00) % 28 !== 0;
	return `${word}${hasFinal ? withFinal : withoutFinal}`;
}

let refreshInFlight:
	| Promise<{ access_token: string; refresh_token: string }>
	| null = null;

export async function refreshToken() {
	if (refreshInFlight) {
		return refreshInFlight;
	}

	refreshInFlight = (async () => {
	const refreshToken = Cookies.get("library-refresh-token");

	if (!refreshToken) {
		throw new Error("Refresh token not found");
	}

	try {
		const response = await ky.get(
			`https://api${import.meta.env.DEV ? "-local" : ""}.library.dxmt.me/auth/refresh`,
			{
				headers: {
					Authorization: `Bearer ${refreshToken}`,
				},
				retry: 0,
				timeout: 10000, // 10초
			},
		);

		const data = (await response.json()) as { success: boolean };

		if (!data.success) {
			throw new Error("Failed to refresh token");
		}

		const access_token = response.headers.get("library-access-token");
		const refresh_token = response.headers.get("library-refresh-token");

		if (!access_token || !refresh_token) {
			throw new Error("Failed to refresh token");
		}

		useDefaultStore.getState().setToken(access_token);
		Cookies.set("library-refresh-token", refresh_token, { expires: 14 });

		return { access_token, refresh_token };
	} catch (error) {
		console.error("Failed to refresh token", error);

		useDefaultStore.getState().setToken(null);
		Cookies.remove("library-refresh-token");

		throw error;
	} finally {
		refreshInFlight = null;
	}
	})();

	return refreshInFlight;
}

export function logout() {
	useDefaultStore.getState().setToken(null);
	Cookies.remove("library-refresh-token");

	window.location.reload();
}
